import {
  getFileExtension,
  isBrowserPaintableImage,
  isVideo,
} from "./file-helper";

/**
 * A preview the browser can paint for a file the person just picked — BEFORE any server
 * has seen it. Photos the engine can decode get their own bytes as an object URL; videos
 * the engine can decode get a poster frame drawn off a `<video>`; everything else (HEIC,
 * TIFF, PDFs, office files, audio) gets nothing and the caller falls back to a type icon
 * until a server rendition exists.
 *
 * Every URL returned was minted by `URL.createObjectURL` and is the caller's to revoke.
 */

/** How long a video gets to decode its first frame before we give up on a poster. */
const POSTER_TIMEOUT_MS = 8_000;
/** Poster's longest edge — a composer chip is ~85 px, so this is already generous. */
const POSTER_MAX_EDGE_PX = 320;
/** Sample a beat in rather than frame 0, which is black on most camera clips. */
const POSTER_SEEK_S = 1;

const PAINTABLE_IMAGE_MIMES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/bmp",
]);

export function isPaintableImageFile(file: File): boolean {
  const mime = file.type.toLowerCase();
  if (mime) return PAINTABLE_IMAGE_MIMES.has(mime);
  return isBrowserPaintableImage(getFileExtension(file.name));
}

export function isVideoFile(file: File): boolean {
  const mime = file.type.toLowerCase();
  if (mime) return mime.startsWith("video/");
  return isVideo(getFileExtension(file.name));
}

export async function createLocalPreviewUrl(
  file: File,
): Promise<string | undefined> {
  if (isPaintableImageFile(file)) return URL.createObjectURL(file);
  if (isVideoFile(file)) return captureVideoPoster(file);
  return undefined;
}

const VIDEO_MIME_BY_EXTENSION: Record<string, string> = {
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
};

/** Draw one early frame of a decodable video to a small JPEG; undefined when it can't. */
async function captureVideoPoster(file: File): Promise<string | undefined> {
  if (typeof document === "undefined") return undefined;
  const video = document.createElement("video");
  // Ask before decoding: an engine that cannot play this container (AVI, MKV, WMV in
  // every browser; anything in a DOM without a media stack) never fires `loadeddata`,
  // and waiting out the timeout for it would delay the icon fallback for nothing.
  const mime =
    file.type || VIDEO_MIME_BY_EXTENSION[getFileExtension(file.name)] || "";
  if (!mime || typeof video.canPlayType !== "function") return undefined;
  if (video.canPlayType(mime) === "") return undefined;
  const sourceUrl = URL.createObjectURL(file);
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = sourceUrl;

  const frame = new Promise<string | undefined>((resolve) => {
    const fail = () => resolve(undefined);
    video.addEventListener("error", fail, { once: true });
    video.addEventListener(
      "loadeddata",
      () => {
        const target = Math.min(POSTER_SEEK_S, (video.duration || 0) / 2);
        video.addEventListener("seeked", () => resolve(drawFrame(video)), {
          once: true,
        });
        // A seek to the current time never fires `seeked` — draw straight away.
        if (video.currentTime === target) resolve(drawFrame(video));
        else video.currentTime = target;
      },
      { once: true },
    );
  });
  const timeout = new Promise<undefined>((resolve) =>
    setTimeout(() => resolve(undefined), POSTER_TIMEOUT_MS),
  );

  try {
    return await Promise.race([frame, timeout]);
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(sourceUrl);
  }
}

function drawFrame(video: HTMLVideoElement): string | undefined {
  const { videoWidth, videoHeight } = video;
  if (!videoWidth || !videoHeight) return undefined;
  const scale = Math.min(
    1,
    POSTER_MAX_EDGE_PX / Math.max(videoWidth, videoHeight),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(videoWidth * scale);
  canvas.height = Math.round(videoHeight * scale);
  const context = canvas.getContext("2d");
  if (!context) return undefined;
  try {
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    // Synchronous on purpose: the frame is tiny and a data URL round-trip would only
    // add a second async hop for a ~20 KB image.
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    return dataUrlToObjectUrl(dataUrl);
  } catch {
    return undefined;
  }
}

function dataUrlToObjectUrl(dataUrl: string): string | undefined {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return undefined;
  const binary = atob(dataUrl.slice(comma + 1));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: "image/jpeg" }));
}
